import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "./axios";
import axios from "axios";
import { logout } from "@/components/auth/AuthSlice";

interface CommonState {
    uploading: boolean;
    error: string | null;
    uploadedUrl: string | null;
}

const initialState: CommonState = {
    uploading: false,
    error: null,
    uploadedUrl: null,
};

interface UploadPayload {
    file: File;
    paperCode: string;
    subjectName: string;
}

export const uploadToS3 = createAsyncThunk(
    "common/uploadToS3",
    async (payload: UploadPayload, { rejectWithValue }) => {
        try {
            const { file, paperCode, subjectName } = payload;

            // 1. Get Presigned URL
            const API_URL = import.meta.env.VITE_REACT_APP_API_URL || 'https://ai-evaluator.srv1240507.hstgr.cloud';
            const presignedRes = await axiosInstance.post(`${API_URL}/api/files/presigned-upload`, {
                paper_code: paperCode,
                subject_name: subjectName,
                files: [{
                    file_name: file.name,
                    file_type: file.type
                }]
            });

            // Handle response which might be an array or object wrapping the file details
            const responseData = presignedRes.data;
            let uploadInfo;

            if (responseData.uploads && Array.isArray(responseData.uploads)) {
                uploadInfo = responseData.uploads[0];
            } else if (responseData.files && Array.isArray(responseData.files)) {
                uploadInfo = responseData.files[0];
            } else if (Array.isArray(responseData)) {
                uploadInfo = responseData[0];
            } else {
                uploadInfo = responseData;
            }

            // Check if it's an S3 POST (fields provided)
            if (uploadInfo.fields && uploadInfo.base_url) {
                const formData = new FormData();
                // Append all fields first
                Object.entries(uploadInfo.fields).forEach(([key, value]) => {
                    formData.append(key, value as string);
                });
                // Append file last
                formData.append("file", file);

                // Perform S3 POST
                await axios.post(uploadInfo.base_url, formData);

                return uploadInfo.url;
            }

            // Fallback to S3 PUT (if presigned_url provided)
            const { presigned_url, file_url } = uploadInfo;

            if (!presigned_url) throw new Error("No presigned URL returned from server");

            // 2. Upload to S3
            // Use standard axios to avoid Auth header injection
            await axios.put(presigned_url, file, {
                headers: {
                    "Content-Type": file.type,
                },
            });

            return file_url;
        } catch (error: any) {
            console.error("Upload error:", error);
            return rejectWithValue(error.response?.data?.message || error.message || "File upload failed");
        }
    }
);

const commonSlice = createSlice({
    name: "common",
    initialState,
    reducers: {
        resetUploadState: (state) => {
            state.uploading = false;
            state.error = null;
            state.uploadedUrl = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(uploadToS3.pending, (state) => {
                state.uploading = true;
                state.error = null;
            })
            .addCase(uploadToS3.fulfilled, (state, action) => {
                state.uploading = false;
                state.uploadedUrl = action.payload;
            })
            .addCase(uploadToS3.rejected, (state, action) => {
                state.uploading = false;
                state.error = action.payload as string;
            })
            // Reset common state on logout
            .addCase(logout, () => initialState);
    },
});

export const { resetUploadState } = commonSlice.actions;
export default commonSlice.reducer;
